
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { InviteUserFormSchema, EMPTY_INVITE_FORM, combineName, type Role } from '@shared/user';
import { Button, Card, Field, Heading, Stack, Text } from '@client/shared/design';
import { useForm, zodValidator, firstFieldError, TextField } from '@client/shared/form/index.ts';
import { messageFor } from '@client/shared/lib/error-message.ts';
import { inviteUser, GROUP_USERS_QUERY_KEY, type GroupUser } from './user-operations.ts';
import { RolePicker } from './RolePicker.tsx';
import styles from './Users.module.css';

export interface InviteUserFormProps {
  onInvited?: (user: GroupUser) => void;
  onCancel?: () => void;
}

export function InviteUserForm({ onInvited, onCancel }: InviteUserFormProps) {
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: inviteUser,
    onSuccess: async (user) => {
      await queryClient.invalidateQueries({ queryKey: GROUP_USERS_QUERY_KEY });
      onInvited?.(user);
    },
  });

  const form = useForm({
    defaultValues: EMPTY_INVITE_FORM,
    validators: { onSubmit: zodValidator(InviteUserFormSchema) },
    onSubmit: async ({ value }) => {
      setSubmitError(null);
      try {
        await mutation.mutateAsync({
          email: value.email.trim().toLowerCase(),
          name: combineName(value.firstName, value.lastName),
          roles: value.roles,
        });
        form.reset();
      } catch (err) {
        setSubmitError(messageFor(err, 'Could not send the invitation. Please try again.'));
      }
    },
  });

  return (
    <Card pad="lg" raised spine as="section" aria-label="Invite a user">
      <Stack gap={5}>
        <Stack gap={1}>
          <Heading level={3}>Invite a user</Heading>
          <Text tone="soft" size="sm">
            They’ll receive an email with a link to sign in. The account stays pending until they
            use it for the first time.
          </Text>
        </Stack>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void form.handleSubmit();
          }}
          noValidate
        >
          <Stack gap={4}>
            <div className={styles.inviteGrid}>
              <form.Field name="firstName">
                {(field) => (
                  <TextField
                    field={field}
                    label="First name"
                    autoComplete="given-name"
                    placeholder="Dana"
                  />
                )}
              </form.Field>

              <form.Field name="lastName">
                {(field) => (
                  <TextField
                    field={field}
                    label="Last name"
                    autoComplete="family-name"
                    placeholder="Reyes"
                  />
                )}
              </form.Field>
            </div>

            <form.Field name="email">
              {(field) => (
                <TextField
                  field={field}
                  label="Email"
                  type="email"
                  autoComplete="off"
                  inputMode="email"
                  placeholder="dana@yourgroup.com"
                />
              )}
            </form.Field>

            <form.Field name="roles">
              {(field) => (
                <Field
                  label="Roles"
                  hint="Roles are cumulative: an owner can do everything an admin and agent can."
                  error={firstFieldError(field.state.meta.errors)}
                >
                  {(wiring) => (
                    <RolePicker
                      id={wiring.id}
                      aria-invalid={wiring['aria-invalid']}
                      aria-describedby={wiring['aria-describedby']}
                      name={field.name}
                      value={field.state.value as Role[]}
                      onChange={(roles) => field.handleChange(roles)}
                    />
                  )}
                </Field>
              )}
            </form.Field>

            {submitError && (
              <Text size="sm" tone="danger" role="alert">
                {submitError}
              </Text>
            )}

            <div className={styles.inviteActions}>
              {onCancel && (
                <Button type="button" variant="ghost" onClick={onCancel}>
                  Cancel
                </Button>
              )}
              <form.Subscribe selector={(s) => s.isSubmitting}>
                {(isSubmitting) => (
                  <Button type="submit" loading={isSubmitting}>
                    Send invitation
                  </Button>
                )}
              </form.Subscribe>
            </div>
          </Stack>
        </form>
      </Stack>
    </Card>
  );
}
